import MenuIcon from "@mui/icons-material/Menu";
import { Breadcrumbs, Link, useMediaQuery } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect, useMemo } from "react";
import { Choose, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { setHeaderVisible, setIsMobile } from "~/redux/reducers/generalReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";

const useStyles = makeStyles((theme) => ({
  menuButton: {
    marginLeft: -theme.spacing(1),
  },
  breadcrumbText: {
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

interface HeaderProps {
  onDrawerToggle: () => void;
}

function Header({ onDrawerToggle }: HeaderProps): JSX.Element {
  const classes = useStyles();
  const title = useAppSelector((s) => s.general.title);
  const breadcrumbs = useAppSelector((s) => s.general.breadcrumbs);
  const { t } = useTranslation("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [dispatch, isMobile]);

  const headerVisible = useMemo(
    () => isMobile || !!breadcrumbs,
    [breadcrumbs, isMobile],
  );

  useEffect(() => {
    dispatch(setHeaderVisible(headerVisible));
  }, [dispatch, headerVisible]);

  return (
    <If condition={headerVisible}>
      <AppBar color="primary" position="sticky" elevation={0} id="app-header">
        <Toolbar>
          <Grid container spacing={1} alignItems="center">
            {isMobile && (
              <Grid item>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={onDrawerToggle}
                  className={classes.menuButton}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
            )}

            <Choose>
              <Choose.When condition={!!breadcrumbs}>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  className={classes.breadcrumbText}
                >
                  {breadcrumbs?.map((x, index) => {
                    const label = "label" in x ? x.label : t(x.key);
                    if (index === breadcrumbs.length - 1) {
                      return (
                        <Typography
                          key={label}
                          style={{ color: "white" }}
                          sx={{
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.body2.fontSize,
                          }}
                        >
                          {label}
                        </Typography>
                      );
                    }
                    return (
                      <Link
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        component={RouterLink as any}
                        color="inherit"
                        to={x.to}
                        key={label}
                        sx={{
                          fontWeight: theme.typography.fontWeightMedium,
                          fontSize: theme.typography.body2.fontSize,
                        }}
                      >
                        {label}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </Choose.When>
              <Choose.Otherwise>
                <Typography variant="h6">{title}</Typography>
              </Choose.Otherwise>
            </Choose>
          </Grid>
        </Toolbar>
      </AppBar>
    </If>
  );
}

export default Header;
